/**
 * Local Feather-style icon set for the account/admin/directory screens.
 *
 * NOTE: the brief asked for Feather from '@expo/vector-icons', but that package
 * is not installed anywhere in this workspace and adding npm packages /
 * touching package.json is forbidden. These are geometric approximations of
 * the Feather glyphs built purely from Views (strokes, rings, wedges), so they
 * render identically on iOS, Android and web with zero new dependencies.
 * API mirrors Feather: <Icon name="bell" size={20} color={...} />.
 */
import React from 'react';
import { View, type StyleProp, type ViewStyle } from 'react-native';

import { useTheme } from '@/theme';

export type IconName =
  | 'alert-circle' | 'alert-triangle' | 'bell' | 'building' | 'calendar'
  | 'check' | 'chevron-left' | 'chevron-right' | 'clock' | 'credit-card'
  | 'download' | 'edit' | 'file-text' | 'filter' | 'globe' | 'home' | 'info'
  | 'key' | 'list' | 'lock' | 'mail' | 'megaphone' | 'message-square'
  | 'package' | 'phone' | 'pin' | 'plus' | 'search' | 'shield' | 'smartphone' | 'sun' | 'moon'
  | 'user' | 'user-plus' | 'users' | 'wrench' | 'x';

const sw = (s: number) => Math.max(1.6, 2 * s);

/** Solid rounded stroke. x/y = top-left before rotation (rotates about centre). */
const bar = (s: number, c: string, x: number, y: number, w: number, h: number, rotate?: string): ViewStyle => ({
  position: 'absolute', left: x * s, top: y * s, width: w * s, height: h * s,
  borderRadius: (Math.min(w, h) * s) / 2, backgroundColor: c,
  ...(rotate ? { transform: [{ rotate }] } : null),
});

const dot = (s: number, c: string, x: number, y: number, d: number): ViewStyle => ({
  position: 'absolute', left: x * s, top: y * s, width: d * s, height: d * s,
  borderRadius: (d * s) / 2, backgroundColor: c,
});

/** Outlined circle. */
const ring = (s: number, c: string, x: number, y: number, d: number, wMul = 1): ViewStyle => ({
  position: 'absolute', left: x * s, top: y * s, width: d * s, height: d * s,
  borderRadius: (d * s) / 2, borderWidth: sw(s) * wMul, borderColor: c,
});

/** Outlined rounded rect. */
const box = (s: number, c: string, x: number, y: number, w: number, h: number, r: number): ViewStyle => ({
  position: 'absolute', left: x * s, top: y * s, width: w * s, height: h * s,
  borderRadius: r * s, borderWidth: sw(s), borderColor: c,
});

const V = (style: ViewStyle, key: number) => <View key={key} style={style} />;

const GLYPHS: Record<IconName, (s: number, c: string) => React.ReactNode> = {
  'check': (s, c) => [V({
    position: 'absolute', left: 5 * s, top: 6.5 * s, width: 14 * s, height: 8 * s,
    borderLeftWidth: sw(s), borderBottomWidth: sw(s), borderColor: c, transform: [{ rotate: '-45deg' }],
  }, 0)],
  'x': (s, c) => [V(bar(s, c, 5, 11, 14, 2, '45deg'), 0), V(bar(s, c, 5, 11, 14, 2, '-45deg'), 1)],
  // sun: centre ring + 4 rays
  'sun': (s, c) => [
    V(ring(s, c, 8, 8, 8), 0),
    V(bar(s, c, 11.2, 2.5, 1.6, 3.5), 1), V(bar(s, c, 11.2, 18, 1.6, 3.5), 2),
    V(bar(s, c, 2.5, 11.2, 3.5, 1.6), 3), V(bar(s, c, 18, 11.2, 3.5, 1.6), 4),
  ],
  // moon: thick open ring with a bite — ring + rotated bar notch reads as crescent at 16-22px
  'moon': (s, c) => [
    V(ring(s, c, 5, 5, 14, 1.4), 0),
    V(bar(s, c, 13, 4, 6, 6, '45deg'), 1),
  ],
  'plus': (s, c) => [V(bar(s, c, 5, 11, 14, 2), 0), V(bar(s, c, 11, 5, 2, 14), 1)],
  'chevron-right': (s, c) => [V({
    position: 'absolute', left: 6.5 * s, top: 7.5 * s, width: 9 * s, height: 9 * s,
    borderTopWidth: sw(s), borderRightWidth: sw(s), borderColor: c, transform: [{ rotate: '45deg' }],
  }, 0)],
  'chevron-left': (s, c) => [V({
    position: 'absolute', left: 8.5 * s, top: 7.5 * s, width: 9 * s, height: 9 * s,
    borderBottomWidth: sw(s), borderLeftWidth: sw(s), borderColor: c, transform: [{ rotate: '45deg' }],
  }, 0)],
  'search': (s, c) => [V(ring(s, c, 4, 4, 12), 0), V(bar(s, c, 13.8, 15.2, 6.5, 2, '45deg'), 1)],
  // Receiver silhouette: a diagonal rounded-rect handle (rotated bar) joining
  // two solid ear/mouth caps — reads as a handset rather than a bare arc.
  'phone': (s, c) => [
    V(bar(s, c, 5.5, 10, 13, 4, '-45deg'), 0),
    V(dot(s, c, 13.5, 3.5, 7), 1),
    V(dot(s, c, 3.5, 13.5, 7), 2),
  ],
  'file-text': (s, c) => [
    V(box(s, c, 5, 3, 14, 18, 2), 0),
    V(bar(s, c, 8.5, 8.5, 7, 1.7), 1), V(bar(s, c, 8.5, 12.5, 7, 1.7), 2), V(bar(s, c, 8.5, 16.5, 4.5, 1.7), 3),
  ],
  'download': (s, c) => [
    V(bar(s, c, 11.1, 4, 1.8, 10), 0),
    V({
      position: 'absolute', left: 7.5 * s, top: 6 * s, width: 9 * s, height: 9 * s,
      borderRightWidth: sw(s), borderBottomWidth: sw(s), borderColor: c, transform: [{ rotate: '45deg' }],
    }, 1),
    V(bar(s, c, 4, 19, 16, 1.8), 2),
  ],
  'megaphone': (s, c) => [
    V({
      position: 'absolute', left: 10 * s, top: 5.5 * s, width: 0, height: 0,
      borderTopWidth: 6.5 * s, borderBottomWidth: 6.5 * s, borderLeftWidth: 9 * s,
      borderTopColor: 'transparent', borderBottomColor: 'transparent', borderLeftColor: c,
    }, 0),
    V(bar(s, c, 5, 8.5, 5, 7), 1), V(bar(s, c, 7, 16.5, 2, 4), 2),
  ],
  'calendar': (s, c) => [
    V(box(s, c, 4, 5, 16, 15.5, 2), 0), V(bar(s, c, 4.8, 9.8, 14.4, 1.7), 1),
    V(bar(s, c, 7.5, 3, 1.8, 4.5), 2), V(bar(s, c, 14.7, 3, 1.8, 4.5), 3),
  ],
  'credit-card': (s, c) => [V(box(s, c, 3, 5.5, 18, 13.5, 2), 0), V(bar(s, c, 3.8, 9.5, 16.4, 2.4), 1)],
  'package': (s, c) => [
    V(box(s, c, 4, 4.5, 16, 16, 2), 0), V(bar(s, c, 4.8, 10.2, 14.4, 1.7), 1), V(bar(s, c, 11.1, 4.8, 1.8, 6), 2),
  ],
  'list': (s, c) => [
    V(dot(s, c, 4.5, 6.1, 2.4), 0), V(bar(s, c, 9, 6.4, 11, 1.7), 1),
    V(dot(s, c, 4.5, 11.1, 2.4), 2), V(bar(s, c, 9, 11.4, 11, 1.7), 3),
    V(dot(s, c, 4.5, 16.1, 2.4), 4), V(bar(s, c, 9, 16.4, 11, 1.7), 5),
  ],
  'message-square': (s, c) => [
    V(box(s, c, 4, 4.5, 16, 13, 3), 0),
    V({
      position: 'absolute', left: 6.5 * s, top: 16.8 * s, width: 0, height: 0,
      borderTopWidth: 5 * s, borderRightWidth: 5 * s, borderTopColor: c, borderRightColor: 'transparent',
    }, 1),
  ],
  'user': (s, c) => [
    V(ring(s, c, 8, 4, 8), 0),
    V({
      position: 'absolute', left: 5 * s, top: 14 * s, width: 14 * s, height: 7 * s,
      borderWidth: sw(s), borderBottomWidth: 0, borderColor: c,
      borderTopLeftRadius: 7 * s, borderTopRightRadius: 7 * s,
    }, 1),
  ],
  'user-plus': (s, c) => [
    V(ring(s, c, 5, 4.5, 7), 0),
    V({
      position: 'absolute', left: 2.5 * s, top: 14 * s, width: 12 * s, height: 6.5 * s,
      borderWidth: sw(s), borderBottomWidth: 0, borderColor: c,
      borderTopLeftRadius: 6 * s, borderTopRightRadius: 6 * s,
    }, 1),
    V(bar(s, c, 15.5, 9.6, 7, 1.7), 2), V(bar(s, c, 18.15, 7, 1.7, 7), 3),
  ],
  'users': (s, c) => [
    V(ring(s, c, 5, 4.5, 7), 0),
    V({
      position: 'absolute', left: 2.5 * s, top: 14 * s, width: 12 * s, height: 6.5 * s,
      borderWidth: sw(s), borderBottomWidth: 0, borderColor: c,
      borderTopLeftRadius: 6 * s, borderTopRightRadius: 6 * s,
    }, 1),
    V(ring(s, c, 15.5, 6, 5), 2),
    V({
      position: 'absolute', left: 15.5 * s, top: 14.5 * s, width: 6 * s, height: 6 * s,
      borderTopWidth: sw(s), borderRightWidth: sw(s), borderColor: c, borderTopRightRadius: 6 * s,
    }, 3),
  ],
  'info': (s, c) => [V(ring(s, c, 3.5, 3.5, 17), 0), V(dot(s, c, 11, 7, 2.3), 1), V(bar(s, c, 11.15, 10.8, 1.8, 6.5), 2)],
  'shield': (s, c) => [V({
    position: 'absolute', left: 5 * s, top: 3.5 * s, width: 14 * s, height: 17 * s,
    borderWidth: sw(s), borderColor: c,
    borderTopLeftRadius: 3 * s, borderTopRightRadius: 3 * s,
    borderBottomLeftRadius: 11 * s, borderBottomRightRadius: 11 * s,
  }, 0)],
  'alert-triangle': (s, c) => [
    V(bar(s, c, 3.5, 19, 17, 1.8), 0),
    V(bar(s, c, -1.7, 11.1, 18.5, 1.8, '-61deg'), 1),
    V(bar(s, c, 7.25, 11.1, 18.5, 1.8, '61deg'), 2),
    V(bar(s, c, 11.15, 9, 1.8, 5), 3), V(dot(s, c, 11.05, 15.8, 2.1), 4),
  ],
  'alert-circle': (s, c) => [V(ring(s, c, 3.5, 3.5, 17), 0), V(bar(s, c, 11.1, 7, 1.8, 6.5), 1), V(dot(s, c, 11, 15.6, 2.2), 2)],
  'wrench': (s, c) => [V(bar(s, c, 4.5, 10.4, 15, 3.2, '-45deg'), 0), V(ring(s, c, 13.5, 3.5, 7, 1.2), 1)],
  'home': (s, c) => [
    V(bar(s, c, 2.1, 6.9, 11.4, 1.8, '-41deg'), 0),
    V(bar(s, c, 10.6, 6.9, 11.4, 1.8, '41deg'), 1),
    V({
      position: 'absolute', left: 6 * s, top: 11 * s, width: 12 * s, height: 9 * s,
      borderWidth: sw(s), borderTopWidth: 0, borderColor: c,
      borderBottomLeftRadius: 1.5 * s, borderBottomRightRadius: 1.5 * s,
    }, 2),
  ],
  'lock': (s, c) => [
    V(box(s, c, 5, 10.5, 14, 9.5, 2.5), 0),
    V({
      position: 'absolute', left: 8.5 * s, top: 4 * s, width: 7 * s, height: 8 * s,
      borderWidth: sw(s), borderBottomWidth: 0, borderColor: c,
      borderTopLeftRadius: 4.5 * s, borderTopRightRadius: 4.5 * s,
    }, 1),
  ],
  'edit': (s, c) => [V(bar(s, c, 4.2, 10.6, 15.6, 2, '-45deg'), 0), V(bar(s, c, 4, 20, 9, 1.8), 1)],
  'pin': (s, c) => [V(ring(s, c, 8, 3.5, 8), 0), V(bar(s, c, 11.1, 11.5, 1.8, 9), 1)],
  'filter': (s, c) => [V(bar(s, c, 4, 6, 16, 1.8), 0), V(bar(s, c, 7, 11.2, 10, 1.8), 1), V(bar(s, c, 10, 16.4, 4, 1.8), 2)],
  'building': (s, c) => [
    V(box(s, c, 5.5, 3.5, 13, 17, 1.5), 0),
    V(dot(s, c, 9, 7.5, 2), 1), V(dot(s, c, 13.2, 7.5, 2), 2),
    V(dot(s, c, 9, 11.5, 2), 3), V(dot(s, c, 13.2, 11.5, 2), 4),
    V(bar(s, c, 10.8, 15.8, 2.4, 4.7), 5),
  ],
  'globe': (s, c) => [
    V(ring(s, c, 3.5, 3.5, 17), 0), V(bar(s, c, 3.9, 11.1, 16.2, 1.7), 1),
    V({
      position: 'absolute', left: 8 * s, top: 3.8 * s, width: 8 * s, height: 16.4 * s,
      borderWidth: sw(s) * 0.85, borderColor: c, borderRadius: 8 * s,
    }, 2),
  ],
  'bell': (s, c) => [
    V({
      position: 'absolute', left: 6 * s, top: 4 * s, width: 12 * s, height: 12 * s,
      borderWidth: sw(s), borderColor: c,
      borderTopLeftRadius: 6 * s, borderTopRightRadius: 6 * s,
      borderBottomLeftRadius: 1.5 * s, borderBottomRightRadius: 1.5 * s,
    }, 0),
    V(bar(s, c, 4.5, 15.6, 15, 1.8), 1), V(dot(s, c, 10.9, 18.6, 2.2), 2),
  ],
  'key': (s, c) => [
    V(ring(s, c, 4, 12.5, 7.5), 0),
    V(bar(s, c, 9.35, 8.1, 11.3, 1.8, '-45deg'), 1),
    V(bar(s, c, 17.8, 7.5, 1.7, 3.6), 2),
  ],
  'clock': (s, c) => [V(ring(s, c, 4, 4, 16), 0), V(bar(s, c, 11.2, 7.5, 1.7, 5.5), 1), V(bar(s, c, 11.2, 11.4, 4.5, 1.7), 2)],
  'mail': (s, c) => [
    V(box(s, c, 3.5, 5.5, 17, 13, 2), 0),
    V(bar(s, c, 3.45, 9.1, 9.6, 1.7, '39deg'), 1),
    V(bar(s, c, 10.95, 9.1, 9.6, 1.7, '-39deg'), 2),
  ],
  'smartphone': (s, c) => [V(box(s, c, 7, 3.5, 10, 17, 2.5), 0), V(dot(s, c, 11, 16.8, 2), 1)],
};

export function Icon({ name, size = 20, color, style }: {
  name: IconName; size?: number; color?: string; style?: StyleProp<ViewStyle>;
}) {
  const { colors } = useTheme();
  const c = color ?? colors.textSecondary;
  const s = size / 24;
  return (
    <View pointerEvents="none" style={[{ width: size, height: size }, style]}>
      {GLYPHS[name](s, c)}
    </View>
  );
}
